import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  IconButton,
  useTheme,
  useMediaQuery,
  Fade
} from '@mui/material';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import background1 from '../assets/images/us-background-1.jpg';
import background2 from '../assets/images/us-background-2.jpg';
import background3 from '../assets/images/us-background-3.jpg';

// Imágenes para el carrusel con textos específicos para cada slide
const carouselImages = [
  { 
    src: background1, 
    alt: "Jeshua",
    header: "QUIENES SOMOS",
    text: "\"Trabajadores de la luz que buscan difundir la energía crística y promover el despertar de la consciencia en la humanidad\"",
    timeout: 5000
  },
  { 
    src: background2, 
    alt: "Jeshua",
    header: "MISIÓN",
    text: "\"Sentirnos orgullosos de aquello que somos\"",
    timeout: 5000
  },
  { 
    src: background3, 
    alt: "Jeshua",
    header: "PROPÓSITO",
    text: "\"Ser guías de otros para que alcancen su máximo potencial\"",
    timeout: 5000
  }
];

function UsPage() {
  const [activeSlide, setActiveSlide] = useState(0);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  // Avance automático de diapositivas cada 10 segundos
  useEffect(() => {
    const currentTimeout = carouselImages[activeSlide].timeout;
    const interval = setInterval(() => {
      setActiveSlide((prev) => (prev + 1) % carouselImages.length);
    }, currentTimeout);
    
    return () => clearInterval(interval);
  }, [activeSlide, carouselImages]);
  
  const handlePrev = () => {
    setActiveSlide((prev) => (prev === 0 ? carouselImages.length - 1 : prev - 1));
  };
  
  const handleNext = () => {
    setActiveSlide((prev) => (prev + 1) % carouselImages.length);
  };
  
  // Efecto para ajustar la altura del carrusel al tamaño de la ventana
  useEffect(() => {
    const adjustHeight = () => {
      // Calculamos la altura de la ventana menos el header (64px) y footer (aproximadamente 300px)
      const headerHeight = 64; // AppBar height
      const footerHeight = 300; // Approximate footer height
      const availableHeight = window.innerHeight - headerHeight - footerHeight;
      
      // Encontramos el contenedor del carrusel y ajustamos su altura
      const carouselContainer = document.getElementById('carousel-container');
      if (carouselContainer) {
        carouselContainer.style.height = `${Math.max(400, availableHeight)}px`;
      }
    };
    
    // Ajustamos la altura inicialmente y cuando cambie el tamaño de la ventana
    adjustHeight();
    window.addEventListener('resize', adjustHeight);
    
    return () => window.removeEventListener('resize', adjustHeight);
  }, []);
  
  return (
    <Box sx={{ width: '100%' }}>
      {/* Contenedor del Carrusel con ID para referencia en el ajuste de altura */}
      <Box
        id="carousel-container"
        sx={{
          position: 'relative',
          width: '100%',
          height: { xs: '50vh', md: '70vh' }, // Altura base antes de que JavaScript la ajuste
          overflow: 'hidden',
        }}
      >
        {/* Diapositivas del Carrusel */}
        {carouselImages.map((slide, index) => (
          <Box
            key={index}
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              opacity: activeSlide === index ? 1 : 0,
              transition: 'opacity 1s ease-in-out',
              zIndex: activeSlide === index ? 1 : 0,
            }}
          >
            <Box
              component="img"
              src={slide.src}
              alt={slide.alt}
              sx={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
              }}
            />
            
            {/* Superposición con Texto específico para cada slide */}
            <Fade in={activeSlide === index} timeout={1000}>
              <Box
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '100%',
                  backgroundColor: 'rgba(0, 0, 0, 0.5)',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 4,
                }}
              >
                <Box
                  sx={{
                    width: { 
                      xs: 'calc(100% - 60px)',
                      sm: 'calc(100% - 120px)'
                    },
                    mx: 'auto',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                  }}
                >
                  {/* Header Text - Ahora en color naranja */}
                  <Typography
                    variant="h3"
                    component="h1"
                    sx={{
                      color: '#ff5722', // Cambiado a naranja
                      textAlign: 'center',
                      fontFamily: "'Ink Free', Arial, sans-serif",
                      fontSize: { xs: '1.5rem', md: '2rem' },
                      textTransform: 'uppercase',
                      fontWeight: 'bold',
                      mb: 4, // Mayor separación entre textos
                    }}
                    dangerouslySetInnerHTML={{ __html: slide.header }}
                  />
                  
                  {/* Main Text - Mantiene el estilo original */}
                  <Typography
                    variant="h4"
                    component="p"
                    sx={{
                      color: 'white',
                      textAlign: 'center',
                      fontFamily: "'Ink Free', Arial, sans-serif",
                      fontSize: { xs: '1rem', md: '1.5rem' },
                      textTransform: 'uppercase',
                    }}
                    dangerouslySetInnerHTML={{ __html: slide.text }}
                  />
                </Box>
              </Box>
            </Fade>
          </Box>
        ))}
        
        {/* Controles de Navegación */}
        <IconButton
          onClick={handlePrev}
          aria-label="Anterior diapositiva"
          sx={{
            position: 'absolute',
            left: 16,
            top: '50%',
            transform: 'translateY(-50%)',
            color: 'white',
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            '&:hover': {
              backgroundColor: 'rgba(0, 0, 0, 0.7)',
            },
            zIndex: 3,
            display: isMobile ? 'none' : 'flex',
          }}
        >
          <ArrowBackIosNewIcon />
        </IconButton>
        <IconButton
          onClick={handleNext}
          aria-label="Siguiente diapositiva"
          sx={{
            position: 'absolute',
            right: 16,
            top: '50%',
            transform: 'translateY(-50%)',
            color: 'white',
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            '&:hover': {
              backgroundColor: 'rgba(0, 0, 0, 0.7)',
            },
            zIndex: 3,
            display: isMobile ? 'none' : 'flex',
          }}
        >
          <ArrowForwardIosIcon />
        </IconButton>
        
        {/* Indicadores de Diapositivas */}
        <Box
          sx={{
            position: 'absolute',
            bottom: 16,
            left: '50%',
            transform: 'translateX(-50%)',
            display: 'flex',
            gap: 1,
            zIndex: 3
          }}
        >
          {carouselImages.map((_, index) => (
            <Box
              key={index}
              onClick={() => setActiveSlide(index)}
              sx={{
                width: 12,
                height: 12,
                borderRadius: '50%',
                backgroundColor: activeSlide === index ? '#ff5722' : 'white',
                cursor: 'pointer',
                transition: 'background-color 0.3s',
              }}
            />
          ))}
        </Box>
      </Box>
    </Box>
  );
}

export default UsPage;